import React from 'react'
import { useSelector } from 'react-redux'
import { usePathParam } from '@chilecompra/react-kit'
import { Skeleton, Table, TableRow, Toast, Typography } from '@chilecompra/react-kit/components'
import ProtectByProfile from '../../components/Security/ProtectByProfile'
import TablePlaceholder from '../../components/Placeholders/TablePlaceholder'
import { PATH_PARAM } from '../../config/settings/regexes'
import routes from '../../config/settings/routes'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import {
  Content,
  FlexRow,
  InfoIcon,
  LinkInternal,
  Separator,
  Strong,
  TableCell,
  Tooltip,
  TooltipIcon
} from './FinalBeneficiarySection.styles'

/**
 * The FinalBeneficiarySection's container.
 */
const FinalBeneficiarySection = () => {
  const { userType } = useAuthorization()
  const dni = usePathParam('rut')

  const { sellerType } = useSelector(state => state.basicSellerInformation)
  const { managers } = useSelector(state => state.companyAdministration)
  const { beneficiaries, error, hasAffidavit, isForcedAffidavit, loading, otherPartners } = useSelector(
    state => state.finalBeneficiaries
  )
  const { error: summaryError } = useSelector(state => state.summarySection)

  const beneficiariesLength = beneficiaries?.length
  const managersLength = managers?.length
  const loadingPage = loading
  const errorPage = error

  if (error?.reason === 'FORBIDDEN_ERROR' || sellerType === 'natural' || summaryError?.reason === 'FORBIDDEN_ERROR') {
    return null
  }

  return (
    <Content>
      <FlexRow>
        <Typography fontWeight="bold" variant="h4">
          Beneficiarios finales de la empresa proveedora
        </Typography>

        <Tooltip
          content={
            <Typography color="white" variant="subtitle1">
              Personas naturales con una participación igual o mayor al 10%. O que directa o indirectamente ejerza
              funciones de dirección o administración de la empresa.
            </Typography>
          }
        >
          <TooltipIcon>
            <InfoIcon />
          </TooltipIcon>
        </Tooltip>
      </FlexRow>

      {loadingPage && (
        <>
          <Skeleton margin="0 0 8px" width="100%" />
          <TablePlaceholder margin="24px 0 0" />
        </>
      )}

      {errorPage && !loadingPage && (
        <Toast
          border
          margin="24px 0 0"
          severity="error"
          textDirection="vertical"
          title="Lo sentimos, estamos presentando problemas con nuestro servidor"
        >
          En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
          más.
        </Toast>
      )}

      {!errorPage && !loadingPage && (
        <>
          <Typography padding="0 0 8px" variant="subtitle">
            {!beneficiariesLength && managersLength
              ? 'Según lo dispuesto en el artículo 16 de la Ley N°19.886 cuando no sea posible identificar a los beneficiarios finales de una persona jurídica conforme las reglas indicadas en dicho artículo, se considerará como tal a las personas naturales que directa o indirectamente ejerzan funciones de dirección o administración.'
              : 'Fuente: Declaración Jurada del Registro de Proveedores ingresada por el proveedor en Mercado Público'}
          </Typography>

          {userType === 'Publico' && (
            <>
              {hasAffidavit ? (
                <>
                  {isForcedAffidavit ? (
                    <>
                      <Typography color="black3" fontWeight="regular" margin="18px 0" variant="body2">
                        En estos momentos no podemos disponer de forma pública la información de esta empresa
                        proveedora.
                      </Typography>

                      <Typography color="black3" fontWeight="regular" margin="18px 0" variant="body2">
                        Si eres comprador, o perteneces a esta empresa, puedes acceder a esta información iniciando
                        sesión en Mercado Público e ingresando a la ficha privada del proveedor.
                      </Typography>
                    </>
                  ) : (
                    !beneficiariesLength &&
                    !managersLength && (
                      <Typography color="black3" fontWeight="regular" margin="18px 0" variant="body2">
                        Esta empresa proveedora <Strong>no posee</Strong> beneficiarios finales.
                      </Typography>
                    )
                  )}
                </>
              ) : (
                <Typography color="black3" fontWeight="regular" margin="18px 0" variant="body2">
                  Esta empresa proveedora no posee información disponible sobre beneficiarios finales.
                </Typography>
              )}
            </>
          )}

          {userType !== 'Publico' && (
            <>
              {hasAffidavit ? (
                <>
                  {isForcedAffidavit ? (
                    <Toast margin="18px 0 0" severity="info">
                      La información de beneficiarios finales se muestra para todas las empresas proveedoras que hayan
                      realizado su Declaración Jurada del Registro de Proveedores a través del sistema. En caso que la
                      declaración haya sido realizada de forma manual, podrías encontrar dicha información en la sección{' '}
                      <LinkInternal to={routes.documents.replace(PATH_PARAM, dni)}>Documentos</LinkInternal>.
                    </Toast>
                  ) : (
                    !beneficiariesLength &&
                    !managersLength && (
                      <Typography color="black3" fontWeight="regular" margin="18px 0" variant="body2">
                        Esta empresa proveedora <Strong>no posee</Strong> beneficiarios finales.
                      </Typography>
                    )
                  )}
                </>
              ) : (
                <Typography color="black3" fontWeight="regular" margin="18px 0" variant="body2">
                  Debido a que la empresa proveedora no cuenta con su Declaración Jurada del Registro de Proveedores
                  firmada, no hay información disponible sobre Beneficiarios finales de la empresa.
                </Typography>
              )}
            </>
          )}

          {hasAffidavit && (beneficiariesLength || managersLength) && (
            <>
              {!!beneficiariesLength && (
                <Typography color="black3" padding="16px 0 8px" variant="body2">
                  Esta información corresponde a los beneficiarios finales declarados por el proveedor. El porcentaje
                  asignado a cada uno de ellos podría presentar diferencias marginales, producto del redondeo automático
                  aplicado por el sistema.
                </Typography>
              )}

              <Table
                accessibility={{ label: 'Detalle Beneficiarios Finales' }}
                disableLatestBottomBorder
                margin="24px 0 0"
              >
                <TableRow>
                  <TableCell>
                    <Typography fontWeight="bold" variant="body1">
                      Nombre
                    </Typography>
                  </TableCell>

                  <ProtectByProfile rule={userType => userType !== 'Publico'}>
                    <TableCell alignment="left" width="137px">
                      <Typography fontWeight="bold" variant="body1">
                        Rut
                      </Typography>
                    </TableCell>
                  </ProtectByProfile>

                  <TableCell alignment="right" width="220px">
                    {!!beneficiariesLength && (
                      <Typography fontWeight="bold" variant="body1">
                        Porcentaje de participación
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>

                {(beneficiariesLength ? beneficiaries : managers)?.map(beneficiary => (
                  <TableRow key={beneficiary.name}>
                    <TableCell>{beneficiary.name}</TableCell>

                    <ProtectByProfile rule={userType => userType !== 'Publico'}>
                      <TableCell alignment="left" width="137px">
                        {beneficiary.dni}
                      </TableCell>
                    </ProtectByProfile>

                    <TableCell alignment="right" width="137px">
                      {beneficiary.percentage}
                    </TableCell>
                  </TableRow>
                ))}

                {otherPartners !== undefined && (
                  <TableRow>
                    <TableCell>
                      {`OTROS SOCIOS (${otherPartners.numberOfPartners} SOCIO${
                        otherPartners.numberOfPartners > 1 ? 'S' : ''
                      })`}
                    </TableCell>

                    <TableCell alignment="right" width="137px">
                      {otherPartners.percentage}
                    </TableCell>
                  </TableRow>
                )}
              </Table>
            </>
          )}

          <Separator />
        </>
      )}
    </Content>
  )
}

export default FinalBeneficiarySection
